
import React from 'react';

interface StructuredDataProps {
  type: 'WebSite' | 'Organization' | 'SportsActivityLocation' | 'Event' | 'Course' | 'FAQPage' | 'Article';
  data: any;
}

const StructuredData: React.FC<StructuredDataProps> = ({ type, data }) => {
  const generateStructuredData = () => {
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://hkpickleplay.com';
    
    switch (type) {
      case 'WebSite':
        return {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "HK PicklePlay",
          "alternateName": "Hong Kong PicklePlay",
          "url": baseUrl,
          "description": "Hong Kong's premier pickleball community platform for courts, sessions, and players",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${baseUrl}/courts?search={search_term_string}`
            },
            "query-input": "required name=search_term_string"
          },
          "sameAs": [
            "https://www.facebook.com/hkpickleplay",
            "https://www.instagram.com/hkpickleplay",
            "https://twitter.com/hkpickleplay"
          ]
        };

      case 'Organization':
        return {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "HK PicklePlay",
          "alternateName": "Hong Kong PicklePlay",
          "url": baseUrl,
          "logo": `${baseUrl}/logo.png`,
          "description": "Hong Kong's premier pickleball community platform connecting players, courts, and sessions",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "HK",
            "addressRegion": "Hong Kong",
            "addressLocality": "Hong Kong"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+852-1234-5678",
            "contactType": "customer service",
            "email": "info@hkpickleplay.com",
            "availableLanguage": ["English", "Chinese"]
          },
          "sameAs": [
            "https://www.facebook.com/hkpickleplay",
            "https://www.instagram.com/hkpickleplay",
            "https://twitter.com/hkpickleplay"
          ],
          "foundingDate": "2023",
          "areaServed": {
            "@type": "Place",
            "name": "Hong Kong"
          }
        };

      case 'SportsActivityLocation':
        return {
          "@context": "https://schema.org",
          "@type": "SportsActivityLocation",
          "name": data.name,
          "description": data.description,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": data.address,
            "addressLocality": data.district,
            "addressRegion": "Hong Kong",
            "addressCountry": "HK"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": data.latitude || 22.3193,
            "longitude": data.longitude || 114.1694
          },
          "telephone": data.phone,
          "url": `${baseUrl}/courts/${data.id}`,
          "sport": "Pickleball",
          "amenityFeature": data.amenities?.map((amenity: string) => ({
            "@type": "LocationFeatureSpecification",
            "name": amenity
          })),
          "aggregateRating": data.rating ? {
            "@type": "AggregateRating",
            "ratingValue": data.rating,
            "reviewCount": data.total_reviews,
            "bestRating": 5,
            "worstRating": 1
          } : undefined,
          "openingHours": data.operating_hours ? [
            `Mo-Fr ${data.operating_hours.weekdays}`,
            `Sa-Su ${data.operating_hours.weekends}`
          ] : undefined
        };

      case 'Event':
        return {
          "@context": "https://schema.org",
          "@type": "SportsEvent",
          "name": data.title,
          "description": data.description,
          "startDate": `${data.date}T${data.startTime}:00+08:00`,
          "endDate": `${data.date}T${data.endTime}:00+08:00`,
          "location": {
            "@type": "SportsActivityLocation",
            "name": data.court,
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "Hong Kong",
              "addressCountry": "HK"
            }
          },
          "organizer": {
            "@type": "Person",
            "name": data.creator
          },
          "sport": "Pickleball",
          "competitor": data.participants?.map((participant: any) => ({
            "@type": "Person",
            "name": participant.name
          })),
          "maximumAttendeeCapacity": data.maxPlayers,
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
          "offers": data.price ? {
            "@type": "Offer",
            "price": data.price.replace(/[^\d.]/g, ''),
            "priceCurrency": "HKD",
            "availability": "https://schema.org/InStock"
          } : undefined
        };

      case 'Course':
        return {
          "@context": "https://schema.org",
          "@type": "Course",
          "name": data.title,
          "description": data.description,
          "provider": {
            "@type": "Organization",
            "name": "HK PicklePlay",
            "url": baseUrl
          },
          "courseCode": data.category,
          "educationalLevel": data.difficulty,
          "timeRequired": data.duration || data.readTime,
          "inLanguage": "en",
          "about": "Pickleball",
          "teaches": data.title,
          "url": `${baseUrl}/learning/${data.id}`,
          "aggregateRating": data.rating ? {
            "@type": "AggregateRating",
            "ratingValue": data.rating,
            "reviewCount": data.views,
            "bestRating": 5,
            "worstRating": 1
          } : undefined
        };

      case 'FAQPage':
        return {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": data.faqs?.map((faq: any) => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        };

      case 'Article':
        return {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": data.title,
          "description": data.description,
          "author": {
            "@type": "Person",
            "name": data.author
          },
          "publisher": {
            "@type": "Organization",
            "name": "HK PicklePlay",
            "logo": {
              "@type": "ImageObject",
              "url": `${baseUrl}/logo.png`
            }
          },
          "datePublished": data.datePublished,
          "dateModified": data.dateModified || data.datePublished,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": data.url
          },
          "image": data.image,
          "articleSection": data.category,
          "wordCount": data.wordCount,
          "inLanguage": "en"
        };

      default:
        return null;
    }
  };

  const structuredData = generateStructuredData();

  if (!structuredData) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData, null, 2)
      }}
    />
  );
};

export default StructuredData;
